import { LinkProps } from '@/types';
import { colors } from '@/constants';
import styled from 'styled-components';
import * as R from '@/constants/systemRoutes';
import ProtectedRoute from './ProtectedRoute';
import React, { lazy, Suspense, useEffect } from 'react';
import { Container, Loading } from '@/components';
import KeepAlive, { AliveScope } from 'react-activation';
import { ModalFlexProvider, ModalAttributesProvider, useUserContext, CorporateFlagsProvider } from '@/hooks';
import { HashRouter, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { CartProvider, CartTotalsProvider, DiscountFlexAmountProvider, DiscountFlexProvider, PricesProvider, StockProvider } from '@/hooks';

interface RouteProps {
  id: string;
  path: string;
  hasCart: boolean;
  element: JSX.Element;
  isProtected: boolean;
}

const LoadingContainer = styled.div`
  flex: 1;
  height: 100vh;
  width: 100%;
  display: grid;
  place-items: center;
  background-color: ${colors.gray.gray0};
`;

const allRoutes = Object.values(R);

const routes = allRoutes
  .filter((r) => r.view)
  .map(({ id, link: path, view, isProtected, hasCart }: LinkProps): RouteProps => {
    const Element = lazy(async () => {
      return import(`@/views`).then((module) => ({
        default: module[view || 'NoMatch'],
      }));
    });

    return {
      id,
      path,
      hasCart: !!hasCart,
      element: (
        <Suspense
          fallback={
            <LoadingContainer>
              <Loading scale={2} />
            </LoadingContainer>
          }
        >
          <Element />
        </Suspense>
      ),
      isProtected: !!isProtected,
    };
  });

const cartRoutes = routes.filter((route) => route.hasCart);
const protectedRoutes = routes.filter((route) => route.isProtected && !route.hasCart);
const publicRoutes = routes.filter((route) => !route.isProtected && !route.hasCart);

const CartRoutesOutlet: React.FC = () => (
  <PricesProvider>
    <StockProvider>
      <DiscountFlexProvider>
        <CartProvider>
          <DiscountFlexAmountProvider>
            <CartTotalsProvider>
              <AliveScope>
                <Outlet />
              </AliveScope>
            </CartTotalsProvider>
          </DiscountFlexAmountProvider>
        </CartProvider>
      </DiscountFlexProvider>
    </StockProvider>
  </PricesProvider>
);

const RouteElement: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Container>
    {children}
  </Container>
);

// 🔹 Componente interno que pode usar useLocation
const AppRoutes: React.FC = () => {
  const location = useLocation();
  const { handleOfflineOrders } = useUserContext();

  useEffect(() => {
    handleOfflineOrders()
  }, [location]);

  return (
    <CorporateFlagsProvider>
      <Routes>
        {/* Rotas protegidas sem carrinho */}
        {protectedRoutes.map((route) => (
          <Route
            key={route.id}
            id={route.id}
            path={route.path}
            element={
              <ProtectedRoute
                requiredAccess={route.id === 'CHECKOUT' ? 'checkout' : route.id === 'SUCCESS' ? 'success' : route.id}
                element={<RouteElement>{route.element}</RouteElement>}
              />
            }
          />
        ))}

        {/* Rotas públicas */}
        {publicRoutes.map((route) => (
          <Route id={route.id} key={route.id} path={route.path} element={<RouteElement>{route.element}</RouteElement>} />
        ))}

        {/* Rotas com carrinho + KeepAlive */}
        <Route element={<CartRoutesOutlet />}>
          {cartRoutes.map((route) => {
            const isCategoryRoute = route.path.includes('categoria');

            // - ECOMMERCE / SEARCH: id fixo
            // - categoria: id inclui o pathname (param da categoria)
            const keepAliveId = isCategoryRoute ? `${route.id}-${location.pathname}` : route.id;

            const ProtectedElement = <ProtectedRoute requiredAccess={route.id} element={(
              <RouteElement>
                <ModalFlexProvider>
                  <ModalAttributesProvider>
                    {route.element}
                  </ModalAttributesProvider>
                </ModalFlexProvider>
              </RouteElement>
            )} />;

            const shouldKeepAlive = route.id === 'ECOMMERCE' || route.id === 'SEARCH' || isCategoryRoute;

            return (
              <Route
                key={route.id}
                path={route.path}
                id={route.id}
                element={
                  shouldKeepAlive ? (
                    <KeepAlive id={keepAliveId} when={true}>
                      {ProtectedElement}
                    </KeepAlive>
                  ) : (
                    ProtectedElement
                  )
                }
              />
            );
          })}
        </Route>
      </Routes>
    </CorporateFlagsProvider>
  );
};

const CustomRoutes: React.FC = () => (
  <HashRouter>
    <AppRoutes />
  </HashRouter>
);

export default React.memo(CustomRoutes);
